import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { OrganizationService } from '../../api/gen/api/organization.service';
import { ParkingSpaceService } from '../../api/gen/api/parkingSpace.service';
import { OrganizationDto } from '../../api/gen/model/organizationDto';
import { ParkingSpaceDto } from '../../api/gen/model/parkingSpaceDto';
import { JjamesButtonComponent, JJamesCardComponent, JJamesAlertComponent, ResponsiveBaseComponent, Role } from 'shared-lib';
import { RoleService } from '../auth/role-service';

@Component({
  selector: 'app-org-managment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    JjamesButtonComponent,
    JJamesCardComponent,
    JJamesAlertComponent
  ],
  templateUrl: './org-managment.component.html',
  styleUrl: './org-managment.component.scss'
})
export class OrgManagmentComponent extends ResponsiveBaseComponent implements OnInit {
  private readonly orgService = inject(OrganizationService);
  private readonly parkingSpaceService = inject(ParkingSpaceService);
  private readonly roleService = inject(RoleService);
  private readonly router = inject(Router);

  organizations = signal<OrganizationDto[]>([]);
  loading = signal(false);
  isAdministrator = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  searchName = signal('');
  totalCount = signal(0);
  pageIndex = signal(0);
  pageSize = signal(25);
  deletingId = signal<string>('');
  /**
   * The spaces this operator may see, for the space filter.
   *
   * The list itself is already scoped server-side to the caller's grants — an organization only
   * appears when it holds a pool parking config on a space this operator was granted — so this
   * dropdown narrows an already-safe list rather than being the thing that makes it safe.
   */
  accessibleSpaces = signal<ParkingSpaceDto[]>([]);
  selectedSpaceId = signal<string>('');
  protected readonly Math = Math;

  /**
   * Names appearing more than once on the current page, lower-cased.
   *
   * Company registration could create a second organization under an existing name (the name
   * lookup was debounced, so a fast submit missed the match). Flagging them is what lets an
   * admin find the extra row and remove it.
   *
   * Scoped to the loaded page on purpose — it never claims a name is unique, only that these
   * particular rows collide.
   */
  duplicateNames = computed(() => {
    const counts = new Map<string, number>();
    for (const org of this.organizations()) {
      const key = (org.name ?? '').trim().toLowerCase();
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([name]) => name));
  });

  isDuplicate(org: OrganizationDto): boolean {
    return this.duplicateNames().has((org.name ?? '').trim().toLowerCase());
  }

  ngOnInit(): void {
    this.roleService.checkAccess(Role.Administrator).then(isAdmin => {
      this.isAdministrator.set(isAdmin);
    });
    this.loadAccessibleSpaces();
    this.loadOrganizations();
  }

  /** Loads the operator's spaces for the filter. A failure leaves the filter empty; it never blocks the list. */
  private loadAccessibleSpaces(): void {
    this.parkingSpaceService.parkingSpaceFilterPost(
      undefined, undefined, undefined, undefined, undefined, undefined, 0, 100, 'Name'
    ).subscribe({
      next: (result) => this.accessibleSpaces.set(result?.items ?? []),
      error: (error) => console.error('Error loading accessible parking spaces:', error)
    });
  }

  onSpaceFilterChange(spaceId: string): void {
    this.selectedSpaceId.set(spaceId);
    this.pageIndex.set(0);
    this.loadOrganizations();
  }

  loginAsOrg(org: OrganizationDto, event: Event): void {
    event.stopPropagation();
    if (!org.id) return;
    localStorage.setItem('active_org_id', org.id);
    localStorage.setItem('active_org_name', org.name || '');

    // Determine target URL for organization-app
    const currentOrigin = window.location.origin;
    const currentHost = window.location.host;
    let targetOrigin = currentOrigin;

    if (currentHost.includes('operator')) {
      targetOrigin = currentOrigin.replace('operator', 'organization');
    } else if (currentHost.includes('op.')) {
      targetOrigin = currentOrigin.replace('op.', 'org.');
    } else if (currentHost.includes('localhost') || currentHost.includes('127.0.0.1')) {
      // In local dev, organization-app is on port 4200 (if served directly) or 4203
      targetOrigin = (window as any).__ORGANIZATION_URL__ || currentOrigin.replace(/:\d+$/, ':4200');
    }

    const targetUrl = `${targetOrigin}/pools?orgId=${org.id}&orgName=${encodeURIComponent(org.name || '')}`;
    window.open(targetUrl, '_blank');
  }

  loadOrganizations(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.orgService.organizationsFilterPost({
      name: this.searchName() || undefined,
      // Intersected server-side with the caller's own grants, so picking a space can only ever
      // narrow the list — it is not a way to reach a space this operator was not granted.
      parkingSpaceId: this.selectedSpaceId() || undefined,
      pageIndex: this.pageIndex(),
      pageSize: this.pageSize(),
      includeDeleted: false
    }).subscribe({
      next: (response) => {
        if (response) {
          this.organizations.set(response.items ?? []);
          this.totalCount.set(response.totalCount ?? 0);
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading organizations:', error);
        this.errorMessage.set('An error occurred while loading organizations');
        this.loading.set(false);
      }
    });
  }

  onSearch(): void {
    this.pageIndex.set(0);
    this.loadOrganizations();
  }

  createOrganization(): void {
    const spaceId = this.selectedSpaceId();
    this.router.navigate(['/org-management/create'], {
      queryParams: spaceId ? { spaceId } : undefined
    });
  }

  /** Bulk import of organizations from a spreadsheet. Administrator only, as is the route. */
  importOrganizations(): void {
    this.router.navigate(['/org-management/import']);
  }

  viewOrganization(orgId: string): void {
    this.router.navigate(['/org-management', orgId]);
  }

  getPoolConfigCount(org: OrganizationDto): number {
    return org.poolParkConfigs?.length ?? 0;
  }

  getParkerCount(org: OrganizationDto): number {
    return org.parkers?.length ?? 0;
  }

  getZoneCount(org: OrganizationDto): number {
    return org.parkingZones?.length ?? 0;
  }

  onPageChange(newIndex: number): void {
    this.pageIndex.set(newIndex);
    this.loadOrganizations();
  }

  /**
   * Deletes the organization together with its memberships (the backend cascades — see
   * DeleteOrganizationHandler). Administrator-only, both by route guard and by the endpoint's
   * own [Authorize] attribute.
   *
   * The confirm spells out the member count because deleting a company that still has members
   * revokes their membership too, and that is not obvious from a row in a list.
   */
  deleteOrganization(org: OrganizationDto, event: Event): void {
    // The row itself navigates to the org; without this the confirm is followed by a navigation.
    event.stopPropagation();

    if (!org.id || this.deletingId()) {
      return;
    }

    const memberCount = this.getParkerCount(org);
    const confirmed = confirm(
      `Delete "${org.name}"?\n\n`
      + `This removes the organization and its ${memberCount} membership(s). `
      + `Parkers and their license plates are kept, only the company link is removed.\n\n`
      + `This cannot be undone from the UI.`);
    if (!confirmed) {
      return;
    }

    this.deletingId.set(org.id);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.orgService.organizationsOrganizationIdDelete(org.id, {}).subscribe({
      next: () => {
        this.successMessage.set(`"${org.name}" and its memberships were deleted.`);
        this.deletingId.set('');
        this.loadOrganizations();
      },
      error: (error) => {
        console.error('Error deleting organization:', error);
        this.errorMessage.set(error?.status === 403
          ? 'Only administrators may delete organizations.'
          : 'An error occurred while deleting the organization');
        this.deletingId.set('');
      }
    });
  }
}
